"use client";

import * as React from "react";
import { format } from "date-fns";
import type { DateRange } from "react-day-picker";

import { Button } from "./button";
import { DatePickerCalendar } from "./date-picker-calendar";
import { getPresetRanges } from "./date-range-presets";
import {
	type DateRangePickerProps,
	resolveDayRangeClick,
} from "./date-range-picker";
import {
	DrawerClose,
	DrawerContent,
	DrawerFooter,
	DrawerNestedRoot,
	DrawerTitle,
	DrawerTrigger,
} from "./drawer";
import { FilterRow } from "./filter-row";
import { ScrollArea } from "./scroll-area";

const DEFAULT_DATE_FORMAT = "MMM d, yyyy";

/** Props for {@link FilterDrawerDateRange}. */
export type FilterDrawerDateRangeProps = DateRangePickerProps & {
	/** Row label shown inside the filter drawer (e.g. "Date range"). */
	label: string;
};

/**
 * Drawer-based date-range filter for mobile / small viewports.
 *
 * Always renders as a {@link FilterRow} trigger + nested drawer containing
 * preset chips and a full-width calendar. Pair with the existing
 * `DateRangePicker` popover at the callsite when you need a desktop variant.
 */
export function FilterDrawerDateRange({
	label,
	dateRange: controlledDateRange,
	onDateRangeChange,
	placeholder,
	minDate,
	maxDate,
	dateFormat = DEFAULT_DATE_FORMAT,
	datePresets,
	disabledDays,
}: FilterDrawerDateRangeProps) {
	const [internalDateRange, setInternalDateRange] = React.useState<
		DateRange | undefined
	>(undefined);
	const [open, setOpen] = React.useState(false);

	const dateRange = controlledDateRange ?? internalDateRange;
	const setDateRange = (range: DateRange | undefined) => {
		setInternalDateRange(range);
		onDateRangeChange?.(range);
	};

	const customPresets = React.useMemo(
		() => getPresetRanges({ minDate, maxDate, options: datePresets }),
		[minDate, maxDate, datePresets],
	);

	const handlePresetSelect = (presetValue: string) => {
		const preset = customPresets.find((p) => p.value === presetValue);
		if (preset) {
			setDateRange(preset.getRange());
		}
	};

	const handleDayClick = (clickedDate: Date) => {
		const next = resolveDayRangeClick(clickedDate, dateRange, disabledDays);
		if (next) {
			setDateRange(next);
		}
	};

	const formatDateLabel = (date: Date) => format(date, dateFormat);

	const summary = (() => {
		if (!dateRange?.from) {
			return placeholder ?? "All";
		}
		if (dateRange.from && dateRange.to) {
			return `${formatDateLabel(dateRange.from)} \u2192 ${formatDateLabel(dateRange.to)}`;
		}
		return `${formatDateLabel(dateRange.from)} \u2192`;
	})();

	const isActive = dateRange?.from != null || dateRange?.to != null;
	const handleClear = () => setDateRange(undefined);

	return (
		<DrawerNestedRoot open={open} onOpenChange={setOpen}>
			<DrawerTrigger asChild>
				<FilterRow label={label} summary={summary} isActive={isActive} />
			</DrawerTrigger>
			<DrawerContent className="max-h-[80vh]">
				<div className="sticky top-0 z-10 flex items-center justify-between gap-2 border-b border-neutral-200 bg-background px-4 py-3">
					<DrawerTitle className="text-base font-semibold text-neutral-900">
						{label}
					</DrawerTitle>
					{isActive && (
						<Button
							type="button"
							variant="ghost"
							size="sm"
							onClick={handleClear}
							className="text-primary"
						>
							Clear
						</Button>
					)}
				</div>

				{customPresets.length > 0 && (
					<ScrollArea className="border-b border-neutral-200">
						<div className="flex w-max gap-2 p-3">
							{customPresets.map((preset) => (
								<Button
									key={preset.value}
									type="button"
									variant="outline"
									size="sm"
									disabled={!preset.isValid}
									onClick={() => handlePresetSelect(preset.value)}
									className="shrink-0"
								>
									{preset.label}
								</Button>
							))}
						</div>
					</ScrollArea>
				)}

				<div className="flex justify-center py-3">
					<DatePickerCalendar
						mode="range"
						dateRange={dateRange}
						onDateRangeChange={setDateRange}
						onDayClick={handleDayClick}
						minDate={minDate}
						maxDate={maxDate}
						disabledDays={disabledDays}
					/>
				</div>

				<DrawerFooter className="sticky bottom-0 border-t border-neutral-200 bg-background">
					<DrawerClose asChild>
						<Button type="button" variant="primary">
							Done
						</Button>
					</DrawerClose>
				</DrawerFooter>
			</DrawerContent>
		</DrawerNestedRoot>
	);
}
FilterDrawerDateRange.displayName = "FilterDrawerDateRange";
